import { Button, Checkbox, Form, Grid, Popup, Text, TextField } from '@approvalmax/ui/src/components';
import { constants } from 'modules/common';
import { useSelector } from 'modules/react-redux';
import { isLoading } from 'pages/requestForm/selectors/pageSelectors';
import { getQBooksContext } from 'pages/requestForm/selectors/qbooks/qbooksSelectors';
import { memo } from 'react';
import bemFactory from 'react-bem-factory';

import { AddressSection, ContactInformationSection } from './components';
import { qaPrefix } from './QBooksCounterpartyCreationPopupContent.constants';
import { useQBooksCounterpartyCreationForm } from './QBooksCounterpartyCreationPopupContent.hooks';
import { messages } from './QBooksCounterpartyCreationPopupContent.messages';
import { FormFieldId } from './QBooksCounterpartyCreationPopupContent.types';

const qa = bemFactory.qa(qaPrefix);
const { qBooksConstants } = constants;

export const QBooksCounterpartyCreationPopupContent = memo(() => {
    const qbooksContext = useSelector(getQBooksContext);
    const loading = useSelector(isLoading);
    const { isSalesInvoice, form, handleSubmit } = useQBooksCounterpartyCreationForm();

    const counterpartyName = isSalesInvoice ? messages.customer : messages.vendor;
    const sameAsBillingAddress = form.watch(FormFieldId.sameAsBillingAddress);

    return (
        <Form form={form} onSubmit={handleSubmit}>
            <Popup.Header
                title={messages.popupTitle({
                    counterparty: counterpartyName,
                })}
                actions={
                    <Button
                        type='submit'
                        formNoValidate
                        data-qa={qa('done-button')}
                        title={messages.doneButtonTitle({ counterparty: counterpartyName })}
                        progress={loading}
                        color='blue80'
                        size='medium'
                    >
                        {messages.doneButtonText}
                    </Button>
                }
            />

            <Popup.Body>
                <Grid gap={16}>
                    <Text font='headline' fontSize='xxsmall' fontWeight='medium' color='midnight80' spacing='20 0 4'>
                        {messages.contactInformationHeader({
                            counterparty: counterpartyName,
                        })}
                    </Text>

                    <ContactInformationSection withCurrency={qbooksContext.isMultiCurrencyEnabled} />

                    <Text font='headline' fontSize='xxsmall' fontWeight='medium' color='midnight80' spacing='4 0'>
                        {isSalesInvoice ? messages.billingAddressHeader : messages.addressHeader}
                    </Text>

                    <AddressSection name={FormFieldId.billingAddress} />

                    {isSalesInvoice && (
                        <>
                            <Grid gridTemplateColumns='max-content auto' alignItems='center' gap={16}>
                                <Text
                                    font='headline'
                                    fontSize='xxsmall'
                                    fontWeight='medium'
                                    color='midnight80'
                                    spacing='4 0'
                                >
                                    {messages.shippingAddressHeader}
                                </Text>

                                <Checkbox name={FormFieldId.sameAsBillingAddress}>
                                    {messages.sameAsBillingAddress}
                                </Checkbox>
                            </Grid>

                            {!sameAsBillingAddress && <AddressSection name={FormFieldId.shippingAddress} />}
                        </>
                    )}

                    {!isSalesInvoice && (
                        <TextField
                            qa={qa('county')}
                            name={FormFieldId.acctNum}
                            label={messages.acctNum}
                            maxLength={qBooksConstants.BUSINESS_NUMBER_MAX_LENGTH}
                            placeholder={messages.acctNumPlaceholder}
                        />
                    )}
                </Grid>
            </Popup.Body>
        </Form>
    );
});

QBooksCounterpartyCreationPopupContent.displayName = 'QBooksCounterpartyCreationPopupContent';
